import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AdminButton,
  AdminCard,
  AdminEmptyState,
  AdminErrorBanner,
  AdminFilterChips,
  AdminFormField,
  AdminMetricCard,
  AdminPagination,
  AdminPageLayout,
  AdminSearchInput,
  AdminSelect,
  AdminStatusBadge,
  AdminTable,
  AdminTd,
  AdminTextarea,
  AdminTextInput,
  AdminToolbar,
  AdminTr,
} from "@/components/admin";
import { TermsMarkdown } from "@/components/terms/TermsMarkdown";
import {
  createAdminTermsPolicy,
  fetchAdminTermsPolicies,
  fetchConnectionConsentTransitionPreview,
  fetchDashboardSummary,
  transitionExistingConnectionConsents,
  type AdminConnectionConsentTransitionPreview,
} from "@/lib/api";
import { formatNullable } from "@/lib/format";
import type { AdminFilterChip } from "@/components/admin";
import type { AdminTermsPolicyItem, AdminTermsPolicySaveRequest } from "@/types";
import { currentPublishedPolicyIds, versionedPublicPath } from "./termsHistory";

type ActiveFilter = "all" | "Y" | "N";

const PAGE_SIZE = 10;

const createEmptyForm = (seed?: AdminTermsPolicyItem | null): AdminTermsPolicySaveRequest => ({
  termsCategoryCode: seed?.termsCategoryCode ?? "",
  termsTypeCode: seed?.termsTypeCode ?? "",
  title: "",
  content: "",
  version: "",
  requiredYn: "Y",
  effectiveDate: "",
  displayOrder: (seed?.displayOrder ?? 0) + 1,
  useYn: "Y",
});

const categoryTone: Record<string, "blue" | "teal" | "violet" | "amber" | "slate"> = {
  SERVICE: "blue",
  PRIVACY: "teal",
  LOCATION: "violet",
  SENSITIVE_INFO: "amber",
  THIRD_PARTY: "slate",
};

const requiredLabel = (requiredYn: string) => (requiredYn === "Y" ? "필수" : "선택");
const activeLabel = (useYn: string) => (useYn === "Y" ? "사용" : "미사용");
const publishLabel = (status: string) => status === "PUBLISHED" ? "게시" : status === "DRAFT" ? "초안" : status;

const TermsPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<AdminTermsPolicyItem[]>([]);
  const [keyword, setKeyword] = useState("");
  const [categoryCode, setCategoryCode] = useState("all");
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [businessDate, setBusinessDate] = useState("");
  const [form, setForm] = useState<AdminTermsPolicySaveRequest>(() => createEmptyForm());
  const [errorMessage, setErrorMessage] = useState("");
  const [transitionPreview, setTransitionPreview] = useState<AdminConnectionConsentTransitionPreview | null>(null);
  const [transitionReasonCode, setTransitionReasonCode] = useState("OPERATOR_REQUEST");
  const [transitionReason, setTransitionReason] = useState("");
  const [transitionConfirmation, setTransitionConfirmation] = useState("");
  const [transitioning, setTransitioning] = useState(false);
  const [transitionMessage, setTransitionMessage] = useState("");

  const loadPolicies = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const [nextItems, dashboard] = await Promise.all([
        fetchAdminTermsPolicies(),
        fetchDashboardSummary(),
      ]);
      setItems(nextItems);
      setBusinessDate(dashboard.businessDate);
    } catch (error) {
      setItems([]);
      setErrorMessage(error instanceof Error ? error.message : "약관 목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPolicies();
  }, [loadPolicies]);

  useEffect(() => {
    setPage(1);
  }, [activeFilter, categoryCode, keyword]);

  const filteredItems = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();

    return items.filter((item) => {
      const matchesKeyword =
        !normalizedKeyword ||
        item.title.toLowerCase().includes(normalizedKeyword) ||
        item.version.toLowerCase().includes(normalizedKeyword) ||
        item.termsCategoryCode.toLowerCase().includes(normalizedKeyword) ||
        item.termsTypeCode.toLowerCase().includes(normalizedKeyword);
      const matchesCategory = categoryCode === "all" || item.termsCategoryCode === categoryCode;
      const matchesActive = activeFilter === "all" || item.useYn === activeFilter;

      return matchesKeyword && matchesCategory && matchesActive;
    });
  }, [activeFilter, categoryCode, items, keyword]);

  const categories = useMemo(() => {
    const uniqueCategories = new Map<string, string>();
    items.forEach((item) => uniqueCategories.set(item.termsCategoryCode, item.termsCategoryName));
    return Array.from(uniqueCategories.entries()).map(([key, label]) => ({ key, label }));
  }, [items]);

  const types = useMemo(() => {
    const uniqueTypes = new Map<string, string>();
    items
      .filter((item) => !form.termsCategoryCode || item.termsCategoryCode === form.termsCategoryCode)
      .forEach((item) => uniqueTypes.set(item.termsTypeCode, item.termsTypeName));

    if (form.termsTypeCode && !uniqueTypes.has(form.termsTypeCode)) {
      uniqueTypes.set(form.termsTypeCode, form.termsTypeCode);
    }

    return Array.from(uniqueTypes.entries()).map(([key, label]) => ({ key, label }));
  }, [form.termsCategoryCode, form.termsTypeCode, items]);

  const categoryChips: AdminFilterChip[] = useMemo(
    () => [
      { key: "all", label: "전체", count: items.length },
      ...categories.map((category) => ({
        key: category.key,
        label: category.label,
        count: items.filter((item) => item.termsCategoryCode === category.key).length,
      })),
    ],
    [categories, items],
  );

  const activeChips: AdminFilterChip[] = useMemo(
    () => [
      { key: "all", label: "전체", count: items.length },
      { key: "Y", label: "사용", count: items.filter((item) => item.useYn === "Y").length },
      { key: "N", label: "미사용", count: items.filter((item) => item.useYn === "N").length },
    ],
    [items],
  );

  const summary = useMemo(
    () => ({
      total: items.length,
      active: items.filter((item) => item.useYn === "Y").length,
      required: items.filter((item) => item.requiredYn === "Y").length,
      published: items.filter((item) => item.publishStatusCode === "PUBLISHED").length,
    }),
    [items],
  );

  const currentPolicyIds = useMemo(
    () => currentPublishedPolicyIds(items, businessDate),
    [businessDate, items],
  );

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedItems = filteredItems.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const openCreateForm = () => {
    setErrorMessage("");
    setForm({ ...createEmptyForm(items.at(-1)), effectiveDate: businessDate });
    setCreateOpen(true);
  };

  const closeCreateForm = () => {
    setCreateOpen(false);
    setSaving(false);
  };

  const updateForm = <K extends keyof AdminTermsPolicySaveRequest>(key: K, value: AdminTermsPolicySaveRequest[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleCreate = async () => {
    if (!form.title.trim() || !form.content.trim() || !form.version.trim() || !form.termsCategoryCode || !form.termsTypeCode) {
      setErrorMessage("약관 분류, 유형, 제목, 버전, 본문을 입력해 주세요.");
      return;
    }

    setSaving(true);
    setErrorMessage("");
    try {
      const saved = await createAdminTermsPolicy({
        ...form,
        title: form.title.trim(),
        content: form.content.trim(),
        version: form.version.trim(),
        termsCategoryCode: form.termsCategoryCode.trim(),
        termsTypeCode: form.termsTypeCode.trim(),
        displayOrder: Number(form.displayOrder),
      });
      setCreateOpen(false);
      await loadPolicies();
      navigate(`/admin/terms/${saved.termsPolicyId}`);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "약관 저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const loadTransitionPreview = async () => {
    setErrorMessage("");
    setTransitionMessage("");
    try {
      setTransitionPreview(await fetchConnectionConsentTransitionPreview());
      setTransitionConfirmation("");
    } catch (error) {
      setTransitionPreview(null);
      setErrorMessage(error instanceof Error ? error.message : "기존 연결 전환 영향을 조회하지 못했습니다.");
    }
  };

  const handleTransition = async () => {
    if (!transitionPreview?.executable || transitionReason.trim().length < 10 || transitionConfirmation !== transitionPreview.confirmationText) return;
    setTransitioning(true);
    setErrorMessage("");
    try {
      const result = await transitionExistingConnectionConsents({
        reasonCode: transitionReasonCode,
        reason: transitionReason.trim(),
        confirmationText: transitionConfirmation,
      });
      setTransitionMessage(`${result.transitionedCount}개 연결을 재동의 필요 상태로 전환했습니다. 감사 로그 #${result.audit.auditLogId}`);
      setTransitionPreview(null);
      setTransitionReason("");
      setTransitionConfirmation("");
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "기존 연결 동의 전환에 실패했습니다.");
    } finally {
      setTransitioning(false);
    }
  };

  return (
    <AdminPageLayout
      title="약관 관리"
      subtitle="약관별 현재 시행본과 과거 버전 이력을 확인하고 관리합니다."
      actions={
        <>
          <AdminButton variant="soft" size="sm" icon="ri-refresh-line" onClick={() => void loadPolicies()} disabled={loading}>
            새로고침
          </AdminButton>
          <AdminButton variant="primary" size="sm" icon="ri-add-line" onClick={openCreateForm}>
            새 약관
          </AdminButton>
        </>
      }
    >
      <div className="space-y-4">
        {errorMessage && <AdminErrorBanner message={errorMessage} />}

        <section className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <AdminMetricCard title="전체 약관" value={`${summary.total}건`} icon="ri-file-list-3-line" tone="blue" />
          <AdminMetricCard title="사용 중" value={`${summary.active}건`} icon="ri-checkbox-circle-line" tone="emerald" />
          <AdminMetricCard title="필수 동의" value={`${summary.required}건`} icon="ri-shield-check-line" tone="orange" />
          <AdminMetricCard title="게시됨" value={`${summary.published}건`} icon="ri-global-line" tone="violet" />
        </section>

        <AdminCard padded>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div><h2 className="text-sm font-bold text-slate-800">기존 가족 연결 재동의 전환</h2><p className="mt-1 text-xs text-slate-500">현재 유효한 필수 재동의 정책이 있을 때 ACTIVE 연결 전체를 CONSENT_REQUIRED로 전환합니다.</p></div>
            <AdminButton variant="soft" size="sm" icon="ri-search-eye-line" disabled={transitioning} onClick={() => void loadTransitionPreview()}>영향 조회</AdminButton>
          </div>
          {transitionMessage && <p className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs font-medium text-emerald-700">{transitionMessage}</p>}
          {transitionPreview && <div className="mt-3 space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <p className="font-semibold">재동의 정책: {transitionPreview.reconsentRequired ? "적용 중" : "없음"} · ACTIVE 연결: {transitionPreview.activeConnectionCount}건 · 실행: {transitionPreview.executable ? "가능" : "차단"}</p>
            {transitionPreview.executable && <>
              <AdminSelect tone="warning" value={transitionReasonCode} onValueChange={setTransitionReasonCode} disabled={transitioning}><option value="OPERATOR_REQUEST">운영 요청</option><option value="DATA_CORRECTION">데이터 교정</option><option value="SECURITY_RESPONSE">보안 대응</option><option value="TEST_ROLLBACK">테스트·원복 검증</option><option value="OTHER">기타</option></AdminSelect>
              <AdminTextarea tone="warning" value={transitionReason} onChange={(event) => setTransitionReason(event.target.value)} disabled={transitioning} maxLength={500} rows={3} placeholder="전환 사유를 10자 이상 입력하세요." />
              <AdminTextInput tone="danger" value={transitionConfirmation} onChange={(event) => setTransitionConfirmation(event.target.value)} disabled={transitioning} placeholder={`확인 문구 입력: ${transitionPreview.confirmationText}`} />
              <AdminButton variant="danger" size="sm" disabled={transitioning || transitionReason.trim().length < 10 || transitionConfirmation !== transitionPreview.confirmationText} onClick={() => void handleTransition()}>{transitioning ? "전환 중..." : "기존 연결 재동의 전환"}</AdminButton>
            </>}
          </div>}
        </AdminCard>

        <AdminCard padded>
          <AdminToolbar
            actions={
              <AdminFilterChips
                chips={activeChips}
                activeKey={activeFilter}
                onChange={(key) => setActiveFilter(key as ActiveFilter)}
                variant="inline"
              />
            }
          >
            <AdminSearchInput value={keyword} onChange={setKeyword} placeholder="약관명, 버전, 유형 코드 검색" aria-label="약관 검색" />
          </AdminToolbar>
          <AdminFilterChips chips={categoryChips} activeKey={categoryCode} onChange={setCategoryCode} variant="inline" className="mt-3" />
        </AdminCard>

        <AdminCard className="overflow-hidden">
          {filteredItems.length === 0 && !loading ? (
            <AdminEmptyState icon="ri-file-search-line" title="약관이 없습니다" description="현재 조건에 맞는 약관 정책이 없습니다." />
          ) : (
            <AdminTable
              headers={["분류", "약관", "버전", "구분", "대상", "필수", "게시", "시행일", "공개 링크", "상세"]}
              loading={loading}
              loadingMessage="약관 목록을 불러오는 중입니다."
            >
              {pagedItems.map((item) => (
                <AdminTr key={item.termsPolicyId} className="hover:bg-slate-50">
                  <AdminTd>
                    <AdminStatusBadge label={item.termsCategoryName} tone={categoryTone[item.termsCategoryCode] ?? "slate"} />
                  </AdminTd>
                  <AdminTd>
                    <div className="text-sm font-semibold text-slate-800">{item.title}</div>
                    <div className="mt-0.5 text-xs text-slate-400">{item.termsTypeCode}</div>
                  </AdminTd>
                  <AdminTd className="text-xs text-slate-600">{item.version}</AdminTd>
                  <AdminTd>
                    <AdminStatusBadge
                      label={currentPolicyIds.has(item.termsPolicyId) ? "현재 시행본" : item.publishStatusCode === "PUBLISHED" ? "과거 이력" : "발행 전"}
                      tone={currentPolicyIds.has(item.termsPolicyId) ? "emerald" : item.publishStatusCode === "PUBLISHED" ? "slate" : "amber"}
                    />
                  </AdminTd>
                  <AdminTd className="text-xs text-slate-500">{item.targetUserTypes.map((value) => value === "GUARDIAN" ? "Keeper" : value === "SENIOR" ? "Senior" : "공통").join(" · ") || "미지정"}</AdminTd>
                  <AdminTd>
                    <AdminStatusBadge label={requiredLabel(item.requiredYn)} tone={item.requiredYn === "Y" ? "orange" : "slate"} />
                  </AdminTd>
                  <AdminTd>
                    <AdminStatusBadge label={publishLabel(item.publishStatusCode)} tone={item.publishStatusCode === "PUBLISHED" ? "emerald" : "amber"} />
                  </AdminTd>
                  <AdminTd className="text-xs text-slate-500">{formatNullable(item.effectiveDate)}</AdminTd>
                  <AdminTd>
                    {item.publishStatusCode === "PUBLISHED" ? (
                      <div className="flex flex-col items-start gap-1">
                        {currentPolicyIds.has(item.termsPolicyId) && <a href={item.publicPath} target="_blank" rel="noreferrer" className="text-xs font-semibold text-blue-600 hover:underline">최신 공개본</a>}
                        <a href={versionedPublicPath(item)} target="_blank" rel="noreferrer" className="text-xs font-semibold text-slate-600 hover:underline">이 버전 공개본</a>
                      </div>
                    ) : <span className="text-xs text-slate-400">비공개</span>}
                  </AdminTd>
                  <AdminTd>
                    <AdminButton variant="soft" size="sm" onClick={() => navigate(`/admin/terms/${item.termsPolicyId}`)}>
                      상세
                    </AdminButton>
                  </AdminTd>
                </AdminTr>
              ))}
            </AdminTable>
          )}
          {filteredItems.length > 0 && (
            <AdminPagination
              currentPage={safePage}
              totalPages={totalPages}
              totalCount={filteredItems.length}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              itemLabel="건"
            />
          )}
        </AdminCard>

        {createOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/35 px-4">
            <div className="w-full max-w-4xl rounded-lg border border-slate-200 bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900">약관 등록</h3>
                  <p className="mt-0.5 text-xs text-slate-400">신규 등록 후 상세 페이지로 이동합니다.</p>
                </div>
                <AdminButton variant="ghost" size="icon" icon="ri-close-line" onClick={closeCreateForm} aria-label="닫기" />
              </div>

              <div className="max-h-[72vh] overflow-y-auto p-5">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <AdminFormField label="분류" htmlFor="terms-category" required>
                    <AdminSelect
                      id="terms-category"
                      value={form.termsCategoryCode}
                      onValueChange={(nextCategory) => {
                        const nextType = items.find((item) => item.termsCategoryCode === nextCategory)?.termsTypeCode ?? "";
                        setForm((current) => ({ ...current, termsCategoryCode: nextCategory, termsTypeCode: nextType }));
                      }}
                    >
                      <option value="">분류 선택</option>
                      {categories.map((category) => (
                        <option key={category.key} value={category.key}>
                          {category.label}
                        </option>
                      ))}
                    </AdminSelect>
                  </AdminFormField>

                  <AdminFormField label="유형" htmlFor="terms-type" required>
                    <AdminSelect
                      id="terms-type"
                      value={form.termsTypeCode}
                      onValueChange={(nextValue) => updateForm("termsTypeCode", nextValue)}
                    >
                      <option value="">유형 선택</option>
                      {types.map((type) => (
                        <option key={type.key} value={type.key}>
                          {type.label}
                        </option>
                      ))}
                    </AdminSelect>
                  </AdminFormField>

                  <AdminFormField label="제목" htmlFor="terms-title" required className="md:col-span-2">
                    <AdminTextInput
                      id="terms-title"
                      value={form.title}
                      onChange={(event) => updateForm("title", event.target.value)}
                      maxLength={150}
                      placeholder="약관 제목"
                    />
                  </AdminFormField>

                  <AdminFormField label="버전" htmlFor="terms-version" required>
                    <AdminTextInput
                      id="terms-version"
                      value={form.version}
                      onChange={(event) => updateForm("version", event.target.value)}
                      maxLength={30}
                      placeholder="예: 2026.05.13"
                    />
                  </AdminFormField>

                  <AdminFormField label="시행일" htmlFor="terms-effective-date" required>
                    <AdminTextInput
                      id="terms-effective-date"
                      type="date"
                      value={form.effectiveDate}
                      onChange={(event) => updateForm("effectiveDate", event.target.value)}
                    />
                  </AdminFormField>

                  <AdminFormField label="필수 여부" htmlFor="terms-required" required>
                    <AdminSelect
                      id="terms-required"
                      value={form.requiredYn}
                      onValueChange={(nextValue) => updateForm("requiredYn", nextValue)}
                    >
                      <option value="Y">필수</option>
                      <option value="N">선택</option>
                    </AdminSelect>
                  </AdminFormField>

                  <AdminFormField label="상태" htmlFor="terms-status" required>
                    <AdminSelect
                      id="terms-status"
                      value={form.useYn}
                      onValueChange={(nextValue) => updateForm("useYn", nextValue)}
                    >
                      <option value="Y">사용</option>
                      <option value="N">미사용</option>
                    </AdminSelect>
                  </AdminFormField>

                  <AdminFormField label="표시 순서" htmlFor="terms-display-order" required>
                    <AdminTextInput
                      id="terms-display-order"
                      type="number"
                      value={form.displayOrder}
                      onChange={(event) => updateForm("displayOrder", Number(event.target.value))}
                      min={0}
                    />
                  </AdminFormField>
                </div>

                <AdminFormField label="본문" htmlFor="terms-content" required className="mt-4">
                  <AdminTextarea
                    id="terms-content"
                    value={form.content}
                    onChange={(event) => updateForm("content", event.target.value)}
                    rows={16}
                    className="p-3 text-sm leading-7"
                    placeholder="약관 본문을 입력하세요."
                  />
                  <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="mb-3 text-xs font-bold text-slate-500">사용자 화면 미리보기</p>
                    <TermsMarkdown content={form.content} />
                  </div>
                </AdminFormField>
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
                <AdminButton variant="secondary" size="md" onClick={closeCreateForm} disabled={saving}>
                  취소
                </AdminButton>
                <AdminButton variant="primary" size="md" icon="ri-save-line" onClick={() => void handleCreate()} disabled={saving}>
                  {saving ? "저장 중" : "저장"}
                </AdminButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminPageLayout>
  );
};

export default TermsPage;
